import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Row, Col, Button } from "reactstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import Flatpickr from "react-flatpickr";

const validationSchema = Yup.object({
  customerId: Yup.string().required("Customer is required"),
  vehicleId: Yup.string().required("Vehicle is required"),
  sessionId: Yup.string().required("Session is required"),
  suppliersId: Yup.string().required("Supplier is required"),
  tripType: Yup.string().required("Trip Type is required"),
  partyType: Yup.string().required("Party Type is required"),
  kmOrTrip: Yup.number().required("Required").typeError("Invalid number"),

  tollCharges: Yup.number().nullable().typeError("Must be a number"),
  otherCharges: Yup.number().nullable().typeError("Must be a number"),
  handlingCharges: Yup.number().nullable().typeError("Must be a number"),
});

const EditTripModal = ({
  isOpen,
  toggle,
  customers,
  vehicles,
  sessions,
  suppliers,
  trip,
  onSubmit,
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);


  if (!trip) return null;

  const customerOptions =
    customers?.map((c) => ({ value: c.id, label: c.name })) || [];
  const vehicleOptions =
    vehicles?.map((v) => ({ value: v.id, label: v.vehicleNo })) || [];
  const sessionOptions = sessions
    ? [{ value: sessions.id, label: sessions.name }]
    : [];

  const supplierOptions =
    suppliers?.map((se) => ({ value: se.id, label: se.name })) || [];

  //   useEffect(() => {
  //     const fullVehicle = vehicles.find((v) => v.id === trip.vehicleId);
  //     setSelectedVehicle(fullVehicle || null);
  //   }, [trip, vehicles]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Edit Trip</ModalHeader>

      <ModalBody>
        <Formik
          enableReinitialize
          initialValues={{
            id: trip.id,
            tripInvoiceNo: trip.tripInvoiceNo || "",
            customerId: trip.customerId || "",
            vehicleId: trip.vehicleId || "",
            sessionId: trip?.sessionId || "",
            suppliersId: trip.suppliersId || "",
            tripType: trip.tripType || "",
            partyType: trip.partyType || "",
            tripDate: trip.tripDate || "",
            kmOrTrip: trip.kmOrTrip || "",
            rate: trip.rate || "",
            amount: trip.amount || "",
            tollCharges: trip.tollCharges || "",
            otherCharges: trip.otherCharges || "",
            handlingCharges: trip.handlingCharges || "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            let cleaned = { ...values };

            ["tollCharges", "otherCharges", "handlingCharges"].forEach(
              (key) => {
                if (!cleaned[key]) delete cleaned[key];
                else cleaned[key] = Number(cleaned[key]);
              }
            );

            cleaned.kmOrTrip = Number(cleaned.kmOrTrip);
            cleaned.rate = Number(cleaned.rate);
            cleaned.amount = Number(cleaned.amount);

            onSubmit(cleaned);
            toggle();
          }}
        >
          {({ values, errors, touched, setFieldValue }) => {
            const recalcTotal = (
              kmOrTripVal = values.kmOrTrip,
              rateVal = values.rate
            ) => {
              if (kmOrTripVal && rateVal) {
                setFieldValue("amount", Number(kmOrTripVal) * Number(rateVal));
              }
            };

            return (
              <Form>
                <Row>
                  {/* LEFT COLUMN */}
                  <Col md={6}>
                    {/* CUSTOMER */}

                    <div className="">
                      <label className="form-label">Invoice No</label>
                      <Field
                        type="text"
                        name="tripInvoiceNo"
                        className="form-control"
                        value={values.tripInvoiceNo}
                        onChange={(e) =>
                          setFieldValue("tripInvoiceNo", e.target.value)
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Customer *</label>
                      <Select
                        options={customerOptions}
                        value={customerOptions.find(
                          (x) => x.value === values.customerId
                        )}
                        onChange={(opt) =>
                          setFieldValue("customerId", opt?.value || "")
                        }
                        placeholder="Select Customer"
                      />
                    </div>

                    {/* VEHICLE */}
                    <div className="mb-3">
                      <label className="form-label">Vehicle *</label>
                      <Select
                        options={vehicleOptions}
                        value={vehicleOptions.find(
                          (x) => x.value === values.vehicleId
                        )}
                        onChange={(opt) => {
                          setFieldValue("vehicleId", opt?.value || "");
                          const fullObj = vehicles.find(
                            (v) => v.id === opt?.value
                          );
                          setSelectedVehicle(fullObj);

                          if (values.tripType === "KM") {
                            setFieldValue("rate", fullObj?.kmRate || "");
                            recalcTotal(values.kmOrTrip, fullObj?.kmRate);
                          } else {
                            setFieldValue("rate", fullObj?.localRate || "");
                            recalcTotal(values.kmOrTrip, fullObj?.localRate);
                          }
                        }}
                        placeholder="Select Vehicle"
                      />
                    </div>

                    {/* TRIP TYPE */}
                    <Row className="mt-3">
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Trip Type *</label>
                          <Select
                            options={[
                              { value: "KM", label: "KM" },
                              { value: "perTrip", label: "TRIP" },
                            ]}
                            value={
                              values.tripType
                                ? {
                                    value: values.tripType,
                                    label: values.tripType,
                                  }
                                : null
                            }
                            onChange={(opt) => {
                              const val = opt?.value || "";
                              setFieldValue("tripType", val);

                              if (val === "KM") {
                                setFieldValue("partyType", "OUTSTATION");
                                const rate = selectedVehicle?.kmRate || "";
                                setFieldValue("rate", rate);
                                recalcTotal(values.kmOrTrip, rate);
                              } else {
                                setFieldValue("partyType", "LOCAL");
                                const rate = selectedVehicle?.localRate || "";
                                setFieldValue("rate", rate);
                                recalcTotal(values.kmOrTrip, rate);
                              }
                            }}
                            placeholder="Select Trip Type"
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Party Type *</label>
                          <Field
                            name="partyType"
                            className="form-control"
                            disabled
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* KM / TRIPS */}
                    <div className="mb-3">
                      <label className="form-label">
                        {values.tripType === "KM"
                          ? "KM Count"
                          : "Number of Trips"}{" "}
                        *
                      </label>
                      <Field
                        type="number"
                        name="kmOrTrip"
                        className={`form-control ${
                          touched.kmOrTrip && errors.kmOrTrip
                            ? "is-invalid"
                            : ""
                        }`}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFieldValue("kmOrTrip", val);
                          recalcTotal(val, values.rate);
                        }}
                      />
                    </div>

                    {/* RATE */}
                    <div className="mb-3">
                      <label className="form-label">Rate (Auto)</label>
                      <Field
                        type="number"
                        name="rate"
                        className="form-control"
                        disabled
                      />
                    </div>
                  </Col>

                  {/* RIGHT COLUMN */}
                  <Col md={6}>
                    {/* PARTY TYPE AUTO */}

                    {/* SESSION */}
                    <div className="mb-3">
                      <label className="form-label">Session *</label>
                      <Select
                        isDisabled={true}
                        options={sessionOptions}
                        value={sessionOptions[0] || null}
                        onChange={(opt) =>
                          setFieldValue("sessionId", opt?.value || "")
                        }
                      />
                    </div>

                    {/* SUPPLIER */}
                    <div className="mb-3">
                      <label className="form-label">Supplier *</label>
                      <Select
                        options={supplierOptions}
                        value={supplierOptions.find(
                          (x) => x.value === values.suppliersId
                        )}
                        onChange={(opt) =>
                          setFieldValue("suppliersId", opt?.value || "")
                        }
                      />
                    </div>

                    {/* TRIP DATE */}
                    <div className="mb-3">
                      <label className="form-label">Trip Date *</label>
                      <Flatpickr
                        options={{ dateFormat: "d-m-Y", maxDate: "today" }}
                        value={values.tripDate}
                        onChange={(dateArr) => {
                          if (dateArr.length > 0) {
                            const d = dateArr[0];
                            const formatted =
                              ("0" + d.getDate()).slice(-2) +
                              "-" +
                              ("0" + (d.getMonth() + 1)).slice(-2) +
                              "-" +
                              d.getFullYear();
                            setFieldValue("tripDate", formatted);
                          }
                        }}
                        className="form-control"
                      />
                    </div>

                    {/* OPTIONAL CHARGES */}
                    {["tollCharges", "otherCharges", "handlingCharges"].map(
                      (field) => (
                        <div className="mb-3" key={field}>
                          <label className="form-label">
                            {field
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (s) => s.toUpperCase())}
                          </label>
                          <Field
                            type="number"
                            name={field}
                            className="form-control"
                          />
                          {touched[field] && errors[field] && (
                            <div className="text-danger small">
                              {errors[field]}
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </Col>

                  {/* TOTAL AMOUNT — Highlighted */}
                  <Col md={6}>
                    {/* <div className="mt-3">
                      <label
                        className="form-label fw-bold"
                        style={{ fontSize: "1.1rem" }}
                      >
                        Total Amount
                      </label>
                      <div
                        className="p-2 rounded"
                        style={{
                          background: "#f7faff",
                          border: "2px solid #0d6efd",
                          boxShadow: "0 0 6px rgba(13, 110, 253, 0.2)",
                        }}
                      >
                        <Field
                          type="number"
                          name="amount"
                          disabled
                          className="form-control fw-bold"
                          style={{
                            fontSize: "1.4rem",
                            color: "#0d6efd",
                            background: "transparent",
                            border: "none",
                            paddingLeft: 0,
                          }}
                        />
                      </div>
                    </div> */}

                    <div className="mt-4">
                      <label className="form-label fw-bold">Total Amount</label>
                      <Field
                        type="number"
                        name="amount"
                        className="form-control fw-bold"
                        disabled
                      />
                    </div>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end mt-4">
                  <Button
                    color="secondary"
                    onClick={toggle}
                    className="me-2 btn-sm"
                  >
                    Cancel
                  </Button>
                  <Button color="success" className="btn-sm" type="submit">
                    Save Changes
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default EditTripModal;
